// @ts-check
import { createLog } from '../utils/logger'
import { TCString } from '@mol-ads/tcf-core'

const log = createLog()

/**
 * @typedef {Object} ChosenStrings
 * @property {string|undefined} tcString
 * @property {string|undefined} addtlConsent
 * @property {string|undefined} cvlVersion
 * @property {string|undefined} nrvString
 * @property {string|undefined} nrvVersion
 */

/**
 * @typedef {Object} RepromptVersions
 * @property {number} repromptVersion
 * @property {number} nextRepromptVersion
 */

/**
 * @param {number} currentRepromptVersionFromClient 
 * @param {number} nextRepromptVersionFromClient 
 * @param {*} tcDataFromStorage 
 * @returns {RepromptVersions}
 */
function selectRepromptVersions (currentRepromptVersionFromClient, nextRepromptVersionFromClient, tcDataFromStorage) {
  const { repromptVersion: repromptVersionFromStorage = 0 } = tcDataFromStorage || {}
  const repromptVersion = Math.max(currentRepromptVersionFromClient, repromptVersionFromStorage)
  const nextRepromptVersion = Math.max(repromptVersion, nextRepromptVersionFromClient)
  log('nextRepromptVersion', nextRepromptVersion)
  return { nextRepromptVersion, repromptVersion }
}
/**
 * @param {string|undefined} tcStringFromClient 
 * @param {string|undefined} addtlConsentFromClient 
 * @param {string|undefined} nrvStringFromClient
 * @param {*} tcDataFromStorage 
 * @returns {ChosenStrings}
 */
function selectUserStrings (tcStringFromClient, addtlConsentFromClient, cvlVersionFromClient, nrvStringFromClient, nrvVersionFromClient, tcDataFromStorage) {
  const { 
    tcString: tcStringFromStorage = undefined,
    nrvString: nrvStringFromStorage = undefined,
    nrvVersion: nrvVersionFromStorage = undefined,
    addtlConsent: addtlConsentFromStorage = undefined,
    acmVersion: cvlVersionFromStorage = undefined
  } = tcDataFromStorage || {}
  const storedStrings = { 
    tcString: tcStringFromStorage,
    nrvString: nrvStringFromStorage,
    nrvVersion: nrvVersionFromStorage,
    addtlConsent: addtlConsentFromStorage,
    cvlVersion: cvlVersionFromStorage
  }
  const clientStrings = {
    tcString: tcStringFromClient,
    nrvString: nrvStringFromClient,
    nrvVersion: nrvVersionFromClient,
    addtlConsent: addtlConsentFromClient,
    cvlVersion: cvlVersionFromClient
  }
  log('clientString', tcStringFromClient)
  log('storedString', tcStringFromStorage)
  const selectStrings = (tcStringFromStorage, tcStringFromClient) => {
    const stored = TCString.decode(tcStringFromStorage).lastUpdated
    const client = TCString.decode(tcStringFromClient).lastUpdated
    log('stored', stored)
    log('client', client)
    return stored > client ? storedStrings : clientStrings
  }

  if (tcStringFromClient && tcStringFromStorage) {
    return selectStrings(tcStringFromStorage, tcStringFromClient)
  } else if (tcStringFromClient) {
    return clientStrings
  } else {
    return storedStrings
  }
}

export class Config {
  /**
   * @type {TCFV2ScriptOpts}
   */
  #config
  /**
   * @param {TCFV2ScriptOpts} config
   */
  constructor (config) {
    this.#config = config
  }

  getGVLVersions (tcString) {
    const { vendorListVersion } = tcString && TCString.decode(tcString) || {}
    log('gvlVersion from tcString', vendorListVersion)
    let gvlUiVersion = parseInt(this.#config.gvlUiVersion)
    const gvlVersion = vendorListVersion || gvlUiVersion
    gvlUiVersion = Math.max(gvlVersion, gvlUiVersion)
    log('selected gvlVersions', { gvlVersion, gvlUiVersion })
    return { gvlVersion, gvlUiVersion }
  }

  /**
   * @param {string|undefined} _cvlVersion 
   */
  getCvlVersions (_cvlVersion) {
    let cvlUiVersion = parseInt(this.#config.cvlUiVersion)
    /** @type {string|number} */
    let cvlVersion
    // this is only necessary because the initial version '0.0.1', all following versions are integers
    if (_cvlVersion && !Number(_cvlVersion)) {
      cvlVersion = _cvlVersion
    } else if (this.#config.cvlVersion && !Number(this.#config.cvlVersion)) {
      cvlVersion = this.#config.cvlVersion
    } else {
      cvlVersion = _cvlVersion ? parseInt(_cvlVersion) : this.#config.cvlVersion ? parseInt(this.#config.cvlVersion) : cvlUiVersion
      cvlUiVersion = cvlVersion ? Math.max(cvlVersion, cvlUiVersion) : cvlUiVersion
    }
    log('selected cvlVersions', { cvlVersion, cvlUiVersion })
    return { cvlVersion, cvlUiVersion }
  }

  getNrvVersions (_nrvVersion) {
    const nrvVersion = _nrvVersion ? parseInt(_nrvVersion) : undefined
    let nrvUiVersion = parseInt(this.#config.nrvUiVersion)
    nrvUiVersion = nrvVersion ? Math.max(nrvVersion, nrvUiVersion) : nrvUiVersion
    log('selected nrvVersions', { nrvVersion, nrvUiVersion })
    return { nrvVersion, nrvUiVersion }
  }

  getUserPreferences (tcData) {
    return {
      ...selectUserStrings(
        this.#config.tcString,
        this.#config.addtlConsent,
        this.#config.cvlVersion,
        this.#config.nrvString, 
        this.#config.nrvVersion,
        tcData),
      ...selectRepromptVersions(this.#config.repromptVersion, this.#config.nextRepromptVersion, tcData)
    }
  }

  get (key) {
    if (!key) return this.#config
    
    if (Object.prototype.hasOwnProperty.call(this.#config, key)) {
      return this.#config[key]
    }
  }
}

export function createConfig () {
  const scriptTag = document.getElementById('mol-ads-tcfv2-script')
  /**
   * @type TCFV2ScriptOpts
   */
  const config = JSON.parse(scriptTag?.dataset.opts || '{}')
  log('config', config)
  return new Config(config)
}

/**
 * @typedef {'Level1' | 'Level2'} UILevel
 */

/**
 * @typedef {Object} TCFV2ScriptOpts
 * @property {string} [addtlConsent]
 * @property {string} [backendUrl]
 * @property {string} [brand]
 * @property {string} [brokerId]
 * @property {string} controllerScriptBase
 * @property {string} [cvlVersion]
 * @property {string} cvlUiVersion
 * @property {string} gvlUiVersion
 * @property {number|string} gvlVersion
 * @property {number} nextRepromptVersion
 * @property {string} [renderPlatform]
 * @property {number} repromptVersion
 * @property {boolean} [showUi]
 * @property {boolean} [storeUserPreference]
 * @property {string} [tcString]
 * @property {UILevel} [uiLevel]
 * @property {string} uiScriptUrl
 * @property {string} [uuid]
 * @property {string} platform
 * @property {string} [nrvString]
 * @property {string} nrvUiVersion
 * @property {string} [nrvVersion]
 */
